/**
 * Present + control tools (docs/agent.md §3).
 *
 * These are the only tools with an effect the user sees immediately. They still
 * never touch the timeline: `show_clips` writes a chat message, and `propose`
 * freezes the op log into a proposal card the user must explicitly Apply. The
 * permission gate is at apply time, which is the only point where anything
 * shared actually changes.
 */
import type { ClipCard, ToolResult } from '../core/types';
import { toolError } from '../core/types';
import { MIN_PLACED_DURATION } from '../../format/partRange';
import type { PresentToolDef } from './catalog';

const MAX_CARDS = 8;

export const showClipsTool: PresentToolDef = {
  name: 'show_clips',
  kind: 'present',
  description:
    'Show candidate clips to the user as cards in the chat, with a short message. Use this to present options you found before assembling anything — the user can preview each one, save it, or add it themselves. Times are CONCAT seconds.',
  schema: {
    type: 'object',
    properties: {
      message: { type: 'string', description: 'one or two sentences introducing the clips' },
      clips: {
        type: 'array',
        maxItems: MAX_CARDS,
        items: {
          type: 'object',
          properties: {
            archive: { type: 'string' },
            item: { type: 'string' },
            title: { type: 'string' },
            in: { type: 'number', minimum: 0 },
            out: { type: 'number', minimum: 0 },
            note: { type: 'string', description: 'why this clip is relevant' },
          },
          required: ['archive', 'item', 'title', 'in', 'out'],
        },
      },
    },
    required: ['message', 'clips'],
  },
  describeCall: (a) => `Show ${(a.clips as unknown[])?.length ?? 0} clips`,
  resultBudget: 300,
  async run(args, ctx) {
    const clips = args.clips as ClipCard[];
    if (!clips.length) {
      return toolError('clips must not be empty — say so in a normal reply if you found nothing');
    }
    const bad = clips.find((c) => c.out < c.in);
    if (bad) {
      return toolError(
        `clip ${bad.item} has out (${bad.out}) before in (${bad.in}); each card needs a real time range`,
      );
    }
    // A card may legitimately be one frame long — that is what a shot
    // description annotation is. Floor it here rather than erroring, for the
    // same reason resolveSource does: an error is an invitation to invent a
    // duration, and the model accepts it every time.
    const carded = clips.map((c) =>
      c.out - c.in < MIN_PLACED_DURATION ? { ...c, out: c.in + MIN_PLACED_DURATION } : c,
    );
    ctx.postChat({
      body: args.message as string,
      atts: [{ type: 'clips', clips: carded }],
    });
    return { ok: true, shown: carded.length };
  },
};

export const proposeTool: PresentToolDef = {
  name: 'propose',
  kind: 'control',
  description:
    'Finish an editing task by proposing your changes to the user. Everything you edited is a draft until you call this, and the user still has to Apply it. Call it exactly once, at the end, when the edit is complete. Keep one proposal to one user intent.',
  schema: {
    type: 'object',
    properties: {
      title: { type: 'string', description: 'short, e.g. "Assemble a 40s monsoon sequence"' },
      summary: {
        type: 'string',
        description: 'one or two sentences on what you did and why',
      },
    },
    required: ['title', 'summary'],
  },
  describeCall: (a) => `Propose: ${a.title}`,
  resultBudget: 300,
  async run(args, ctx) {
    const id = ctx.proposeOps(args.title as string, args.summary as string);
    if (!id) {
      return toolError(
        'there are no changes to propose yet — make the edit first, then call propose',
      );
    }
    return {
      ok: true,
      proposalId: id,
      note: 'proposal posted; the user will preview and apply it. Do not repeat these edits.',
    } as ToolResult;
  },
};

export const PRESENT_TOOLS: PresentToolDef[] = [showClipsTool, proposeTool];
